import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, makeStyles, Table, TableHead, TableBody, TableRow, TableCell, TableFooter, TablePagination } from "@material-ui/core";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import AppDialog from "components/Dialog/AppDialog";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getPageAppraisal } from "store/slices/appraisal";
import AppConfig from "config/AppConfig";
import AppraisalModal from "../modal/AppraisalModal";
import moment from "moment";
import ComponentLoading from "components/Common/ComponentLoading";
import TablePaginationActions from "../../Table/TablePaginationActions";
import { getAuthInfo } from "store/slices/auth";
import { SYSTEM_ROLE } from "config/AppConfig";

const useStyles = makeStyles((theme) => ({
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "0 0 10px 0",
    paddingBottom: 5,
    border: "none",
  },
  alertBox: {
    background: "#F0FDFA",
    border: "1px solid #99F6E4",
    color: "#0D9488",
    borderRadius: 6,
    padding: "9px 11px",
    fontSize: 11,
    marginBottom: 10,
    display: "flex",
    gap: 7,
    alignItems: "flex-start",
    lineHeight: 1.5,
  },
  card: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    marginBottom: 16,
  },
  cardHeader: {
    padding: "8px 12px",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#FAFAFA",
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: "#111827",
  },
  cardContent: {
    padding: "12px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    marginBottom: 10,
  },
  tableHead: {
    background: "#FAFAFA",
  },
  tableCell: {
    padding: "8px 10px",
    textAlign: "left",
    fontWeight: 600,
    color: "#6B7280",
    borderBottom: "1px solid #E5E7EB",
    fontSize: 11,
    whiteSpace: "nowrap",
  },
  tableBodyCell: {
    padding: "8px 10px",
    borderBottom: "1px solid #F3F4F6",
    color: "#111827",
    verticalAlign: "middle",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 16,
  },
  label: {
    fontSize: 11,
    fontWeight: 500,
    color: "#374151",
    marginBottom: 5,
    display: "block",
  },
  donePill: {
    background: "#CCFBF1",
    color: "#0F766E",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 7px",
    borderRadius: 5,
    fontSize: 11,
    fontWeight: 500,
  },
  loadingBox: {
    padding: 20,
    display: "flex",
    justifyContent: "center",
  },
  customTableFooter: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
    '& .MuiToolbar-root': {
        '& .MuiTablePagination-spacer': {
            display: 'none'
        }
    },
    "& .MuiTablePagination-root": {
        overflow: 'visible'
    }
  },
}));

const AppraisalTab = ({ staffDetail }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [showAddModal, setShowAddModal] = useState(false);
  const [currentAppraisal, setCurrentAppraisal] = useState(null);
  const [pastAppraisals, setPastAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPast, setLoadingPast] = useState(false);
  const [viewingPastAppraisal, setViewingPastAppraisal] = useState(null);
  const authInfo = useSelector(getAuthInfo);
  const isAdmin = authInfo?.role === SYSTEM_ROLE.COMPANY;

  const [queries, setQueries] = useState({
    page: 1,
    size: 10,
  });
  const [totalElements, setTotalElements] = useState(0);

  const fetchAppraisalsCurrent = useCallback(async () => {
    if (!staffDetail?.id) return;
    setLoading(true);

    try {
      const currentRes = await dispatch(getPageAppraisal({
        workerId: staffDetail.id,
        status: AppConfig.APPRAISAL_STATUS.CURRENT,
        pageSize: 1,
      })).unwrap();

      if (currentRes?.content?.length > 0) {
        setCurrentAppraisal(currentRes.content[0]);
      } else {
        setCurrentAppraisal(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [dispatch, staffDetail]);

  const fetchAppraisalsPast = async ({ page, size } = {}) => {
    if (!staffDetail?.id) return;
    setLoadingPast(true);

    const newQueries = {
        ...queries,
    };
    if (page) {
        newQueries.page = page;
    }
    if (size) {
        newQueries.size = size;
    }

    try {
      const pastRes = await dispatch(getPageAppraisal({
        workerId: staffDetail.id,
        status: AppConfig.APPRAISAL_STATUS.ARCHIVE,
        pageSize: newQueries.size,
        pageNumber: newQueries.page
      })).unwrap();

      if (pastRes) {
        setQueries(newQueries);
        setPastAppraisals(pastRes.content || []);
        setTotalElements(pastRes.totalElements || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPast(false);
    }
  };

  useEffect(() => {
    fetchAppraisalsCurrent();
    fetchAppraisalsPast();
  }, [staffDetail]);

  const handleChangePage = (event, newPage) => {
    fetchAppraisalsPast({ page: newPage + 1 });
  };

  const handleChangeRowsPerPage = (event) => {
    fetchAppraisalsPast({
        page: 1,
        size: parseInt(event.target.value),
    });
  };

  const renderRatingLabel = (val) => {
    if (!val) return "";
    return t(`appraisalRating.${val}`) || val;
  };

  const getFormatDate = (date) => {
    if (!date) return "";
    return moment(date).format("DD MMM YYYY");
  };

  const renderAppraisalDetails = (appraisal) => {
    if (!appraisal) return null;

    const criteriaList = [
      { key: "shiftAttendance", label: t("appraisalCriteria.shiftAttendance"), value: appraisal.shiftAttendance },
      { key: "carePlanImplementation", label: t("appraisalCriteria.carePlanImplementation"), value: appraisal.carePlanImplementation },
      { key: "incidentReporting", label: t("appraisalCriteria.incidentReporting"), value: appraisal.incidentReporting },
      { key: "ndisCompliance", label: t("appraisalCriteria.ndisCompliance"), value: appraisal.ndisCompliance },
    ];

    return (
      <Box>
        {/* Criteria Ratings Section */}
        <Box mb={2}>
          <Typography style={{ fontSize: 11, fontWeight: 600, color: "#4B5563", textTransform: "uppercase", marginBottom: 12 }}>
            {t("appraisalLabel.criteriaRatings")}
          </Typography>
          <Box style={{ gap: "16px 24px" }}>
            {criteriaList.map((item, index) => {
              let progressPercent = 0;
              // let color = "#CBD5E1";
              const color = "#10B981"; // Green
              if (item.value === AppConfig.APPRAISAL_RATING.EXCEEDS) {
                progressPercent = 100;
              } else if (item.value === AppConfig.APPRAISAL_RATING.MEETS) {
                progressPercent = 66;
              } else if (item.value === AppConfig.APPRAISAL_RATING.BELOW) {
                progressPercent = 33;
              }
              
              return (
                <Box key={index} marginBottom={4}>
                  <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <Typography style={{ fontSize: 11, fontWeight: 500, color: "#374151" }}>
                      {item.label}
                    </Typography>
                    <Typography style={{ fontSize: 11, fontWeight: 600, color: color }}>
                      {renderRatingLabel(item.value) || "-"}
                    </Typography>
                  </Box>
                  <Box style={{ width: "100%", height: 6, backgroundColor: "#E5E7EB", borderRadius: 3 }}>
                    <Box style={{ width: `${progressPercent}%`, height: "100%", backgroundColor: color, borderRadius: 3 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
        {/* Overall Rating Block */}
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "#F9FAFB", borderRadius: 6, marginBottom: 16, border: "1px solid #E5E7EB" }}>
          <Typography style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
            {t("appraisalLabel.overallRating")}
          </Typography>
          <span style={{
            fontSize: 12,
            fontWeight: 600,
            color: appraisal.overallRating === AppConfig.APPRAISAL_RATING.EXCEEDS ? "#10B981" :
                   appraisal.overallRating === AppConfig.APPRAISAL_RATING.MEETS ? "#3B82F6" :
                   appraisal.overallRating === AppConfig.APPRAISAL_RATING.NEEDS_IMPROVEMENT ? "#d97706" :
                   appraisal.overallRating === AppConfig.APPRAISAL_RATING.BELOW ? "#EF4444" : "#6B7280",
            background: appraisal.overallRating === AppConfig.APPRAISAL_RATING.EXCEEDS ? "#D1FAE5" :
                        appraisal.overallRating === AppConfig.APPRAISAL_RATING.MEETS ? "#DBEAFE" :
                        appraisal.overallRating === AppConfig.APPRAISAL_RATING.NEEDS_IMPROVEMENT ? "#f7edc2" :
                        appraisal.overallRating === AppConfig.APPRAISAL_RATING.BELOW ? "#FEE2E2" : "#F3F4F6",
            padding: "4px 10px",
            borderRadius: 12
          }}>
            {renderRatingLabel(appraisal.overallRating) || "-"}
          </span>
        </Box>

        {/* Notes / Cards Section */}
        <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
          {/* Strengths Noted Card */}
          <Box style={{ background: "#F0FDF4", border: "1px solid #DCFCE7", borderRadius: 6, padding: 12 }}>
            <Box style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <i className="fas fa-check-circle" style={{ color: "#16A34A", fontSize: 14 }}></i>
              <Typography style={{ fontSize: 10, fontWeight: 700, color: "#16A34A", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {t("appraisalLabel.strengthsNoted")}
              </Typography>
            </Box>
            <Typography style={{ fontSize: 11, color: "#1F2937", whiteSpace: "pre-line", lineHeight: 1.5 }}>
              {appraisal.strengths || "-"}
            </Typography>
          </Box>

          {/* Areas for Development Card */}
          <Box style={{ background: "#FFFBEB", border: "1px solid #FEF3C7", borderRadius: 6, padding: 12 }}>
            <Box style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <i className="fas fa-chart-line" style={{ color: "#D97706", fontSize: 14 }}></i>
              <Typography style={{ fontSize: 10, fontWeight: 700, color: "#D97706", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {t("appraisalLabel.areasForDevelopment")}
              </Typography>
            </Box>
            <Typography style={{ fontSize: 11, color: "#1F2937", whiteSpace: "pre-line", lineHeight: 1.5 }}>
              {appraisal.areasDevelopment || "-"}
            </Typography>
          </Box>
        </Box>

        {/* Agreed Actions Card (Full Width) */}
        <Box style={{ background: "#EFF6FF", border: "1px solid #DBEAFE", borderRadius: 6, padding: 12, marginTop: 16 }}>
          <Box style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <i className="fas fa-arrow-right" style={{ color: "#2563EB", fontSize: 14 }}></i>
            <Typography style={{ fontSize: 10, fontWeight: 700, color: "#2563EB", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {t("appraisalLabel.agreedActions")}
            </Typography>
          </Box>
          <Typography style={{ fontSize: 11, color: "#1F2937", whiteSpace: "pre-line", lineHeight: 1.5 }}>
            {appraisal.agreedActions || "-"}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box className={classes.loadingBox}>
        <ComponentLoading />
      </Box>
    );
  }

  return (
    <Box>
      <Box className={classes.headerRow}>
        <Typography className={classes.sectionTitle}>
          {t("appraisalLabel.performanceAppraisal")}
        </Typography>
        {
          isAdmin &&
          <AppButton
            label={t("appraisalLabel.newAppraisal")}
            buttonColor={AppColor.mainColor}
            style={{ fontSize: 11, padding: "4px 10px" }}
            rounder
            startIcon={<i className="fas fa-plus" style={{ fontSize: 12 }}></i>}
            onClick={() => setShowAddModal(true)}
          />
        }
      </Box>

      {staffDetail?.startDate && currentAppraisal && (
        <Box className={classes.alertBox}>
          <Box>
            <div>
              {t("appraisalLabel.summaryAlert", {
                lastAppraisal: currentAppraisal.completedDate ? moment(currentAppraisal.completedDate).format("D MMM YYYY") : "-",
                nextDue: currentAppraisal.dueDate ? moment(currentAppraisal.dueDate).format("D MMM YYYY") : "-",
                conductedBy: currentAppraisal.reviewedBy || "Admin"
              })}
            </div>
          </Box>
        </Box>
      )}

      {currentAppraisal ? (
        <Box className={classes.card}>
          <Box className={classes.cardHeader}>
            <Typography className={classes.cardTitle}>
              {t("appraisalLabel.currentAppraisal")} {currentAppraisal.completedDate ? `— ${getFormatDate(currentAppraisal.completedDate)}` : ""}
            </Typography>
            <span style={{ fontSize: 11, color: "#6B7280", background: "#F3F4F6", padding: "2px 8px", borderRadius: 4 }}>
              {t("appraisalLabel.conductedBy")}: {currentAppraisal.reviewedBy || "Admin"}
            </span>
          </Box>
          <Box className={classes.cardContent}>
            {renderAppraisalDetails(currentAppraisal)}
          </Box>
        </Box>
      ) : (
        <Box className={classes.card}>
          <Box className={classes.cardContent}>
            <Typography variant="body2" color="textSecondary" align="center">
              {t("appraisalLabel.noCurrentAppraisal")}
            </Typography>
          </Box>
        </Box>
      )}

      <Typography className={classes.sectionTitle}>
        {t("appraisalLabel.pastAppraisals")}
      </Typography>

      <Box className={classes.card}>
        <Box style={{ overflowX: "auto" }}>
          {loadingPast && pastAppraisals.length === 0 ? (
            <Box className={classes.loadingBox}>
              <ComponentLoading />
            </Box>
          ) : (
            <Table className={classes.table}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableCell} style={{ width: "30%" }}>{t("appraisalLabel.date")}</TableCell>
                  <TableCell className={classes.tableCell} style={{ width: "40%" }}>{t("appraisalLabel.conductedBy")}</TableCell>
                  <TableCell className={classes.tableCell} style={{ width: "20%" }}>{t("appraisalLabel.overallRating")}</TableCell>
                  <TableCell className={classes.tableCell} style={{ width: "10%" }}>{t("appraisalLabel.action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pastAppraisals.length > 0 ? (
                  pastAppraisals.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell className={classes.tableBodyCell}>{getFormatDate(row.completedDate)}</TableCell>
                      <TableCell className={classes.tableBodyCell}>{row.reviewedBy}</TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        <span className={classes.donePill}>{renderRatingLabel(row.overallRating)}</span>
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        <i
                          className="fas fa-eye"
                          style={{ cursor: "pointer" }}
                          onClick={() => setViewingPastAppraisal(row)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className={classes.tableBodyCell} align="center">
                      {t("appraisalLabel.noPastAppraisals")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {totalElements > 0 && (
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            colSpan={4}
                            count={totalElements}
                            rowsPerPage={queries.size}
                            page={queries.page - 1}
                            SelectProps={{
                                inputProps: { "aria-label": "rows per page" },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelRowsPerPage={`${t("label.rows_per_page")}:`}
                            labelDisplayedRows={({ from, to, count }) =>
                                t("label.from_to", {
                                    from,
                                    to,
                                    count:
                                        count !== -1 ? count : `${t("text.more_than")} ${to}`,
                                })
                            }
                            style={{ overflowX: 'hidden' }}
                            className={classes.customTableFooter}
                        />
                    </TableRow>
                </TableFooter>
              )}
            </Table>
          )}
        </Box>
      </Box>

      {
        isAdmin && 
        <AppDialog
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          title={t("appraisalLabel.newAppraisal")}
          noPadding
          maxWidth="md"
        >
          <AppraisalModal 
            open={showAddModal} 
            onClose={() => setShowAddModal(false)}
            staffDetail={staffDetail}
            onSuccess={() => {
              fetchAppraisalsCurrent();
              fetchAppraisalsPast({ page: 1 });
            }}
          />
        </AppDialog>
      }

      <AppDialog
        open={Boolean(viewingPastAppraisal)}
        onClose={() => setViewingPastAppraisal(null)}
        title={`${t("appraisalLabel.performanceAppraisal")} — ${viewingPastAppraisal ? getFormatDate(viewingPastAppraisal.completedDate) : ""}`}
        noPadding
        maxWidth="md"
      >
        <Box style={{ padding: "0 20px" }}>
          {renderAppraisalDetails(viewingPastAppraisal)}
        </Box>
      </AppDialog>
    </Box>
  );
};

export default AppraisalTab;
